import { ApolloError } from 'apollo-client';
import PropTypes from 'prop-types';

export const videoShape = PropTypes.shape({
  etag: PropTypes.string,
  kind: PropTypes.string,
  id: PropTypes.shape({
    kind: PropTypes.string,
    videoId: PropTypes.string
  }),
  snippet: PropTypes.shape({
    channelId: PropTypes.string,
    channelTitle: PropTypes.string,
    description: PropTypes.string,
    liveBroadcastContent: PropTypes.string,
    publishedAt: PropTypes.string,
    title: PropTypes.string,
    thumbnails: PropTypes.shape({
      default: PropTypes.shape({
        height: PropTypes.number,
        url: PropTypes.string,
        width: PropTypes.number
      }),
      high: PropTypes.shape({
        height: PropTypes.number,
        url: PropTypes.string,
        width: PropTypes.number
      }),
      medium: PropTypes.shape({
        height: PropTypes.number,
        url: PropTypes.string,
        width: PropTypes.number
      })
    })
  })
});

export const userShape = PropTypes.shape({
  userId: PropTypes.string,
  displayName: PropTypes.string,
  email: PropTypes.string,
  roles: PropTypes.arrayOf(PropTypes.string),
  avatarUrl: PropTypes.string,
  creationDate: PropTypes.string,
  avatarData: PropTypes.string
});

export const displayUserShape = PropTypes.shape({
  userId: PropTypes.string,
  displayName: PropTypes.string,
  avatarUrl: PropTypes.string
});

export const wallCommentShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  comment: PropTypes.string,
  created: PropTypes.string,
  user: userShape
});

export const wallPostShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  user: userShape,
  message: PropTypes.string,
  device: PropTypes.string,
  deviceInfo: PropTypes.string,
  created: PropTypes.string,
  isPinned: PropTypes.bool,
  isLocked: PropTypes.bool,
  isSystemPost: PropTypes.bool,
  comments: PropTypes.shape({
    comments: PropTypes.arrayOf(wallCommentShape)
  })
});

export const communityMessageShape = PropTypes.shape({
  id: PropTypes.number,
  start: PropTypes.string,
  end: PropTypes.string,
  durationSec: PropTypes.number,
  message: PropTypes.string,
  properties: PropTypes.object
});

export const chatMessageShape = PropTypes.shape({
  id: PropTypes.number,
  room: PropTypes.string,
  message: PropTypes.string,
  created: PropTypes.string,
  author: userShape
});

export const gameShape = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  displayName: PropTypes.string,
  description: PropTypes.string,
  enabled: PropTypes.bool
});

export const tournamentShape = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  description: PropTypes.string,
  startTime: PropTypes.string,
  endTime: PropTypes.string,
  status: PropTypes.string,
  game: gameShape
});

export const tournamentsShape = PropTypes.shape({
  tournaments: PropTypes.arrayOf(tournamentShape)
});

export const patronStatusShape = PropTypes.shape({
  patronAccountNumber: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  freePlay: PropTypes.number,
  shoppingDollars: PropTypes.number,
  basePoints: PropTypes.number,
  clubState: PropTypes.string,
  extClubState: PropTypes.string,
  pointsNeeded: PropTypes.number,
  basePointsEarned: PropTypes.number,
  email: PropTypes.string,
  birthDate: PropTypes.string,
  rewardsJoinDate: PropTypes.string,
  homePhone: PropTypes.string,
  mobilePhone: PropTypes.string,
  gender: PropTypes.string,
  address1: PropTypes.string,
  address2: PropTypes.string,
  city: PropTypes.string,
  stateProvince: PropTypes.string,
  postalCode: PropTypes.string
});

export const reservationShape = PropTypes.shape({
  id: PropTypes.number,
  reservationId: PropTypes.number,
  hotelCode: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  total: PropTypes.number,
  profileId: PropTypes.number,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  accountNumber: PropTypes.string,
  status: PropTypes.string
});

export const errorShape = PropTypes.oneOfType([
  PropTypes.instanceOf(Error),
  PropTypes.instanceOf(ApolloError)
]);

export const eventCategoryShape = PropTypes.shape({
  displayName: PropTypes.string,
  eventCategoryId: PropTypes.number,
  lastModified: PropTypes.string,
  value: PropTypes.string
});

export const recurringPatternShape = PropTypes.shape({
  dayOfMonth: PropTypes.number,
  daysOfWeek: PropTypes.arrayOf(PropTypes.number),
  maxNumberOfOccurrences: PropTypes.number,
  monthOfYear: PropTypes.number,
  recurringType: PropTypes.string,
  separationCount: PropTypes.number,
  weekOfMonth: PropTypes.number
});

export const eventShape = PropTypes.shape({
  category: eventCategoryShape,
  createdDate: PropTypes.string,
  detailsUrl: PropTypes.string,
  earnPeriodEndDate: PropTypes.string,
  earnPeriodEndTime: PropTypes.string,
  earnPeriodDescription: PropTypes.string,
  earnPeriodImageUrl: PropTypes.string,
  earnPeriodRecurringPattern: recurringPatternShape,
  earnPeriodStartDate: PropTypes.string,
  earnPeriodStartTime: PropTypes.string,
  enabled: PropTypes.bool,
  endDate: PropTypes.string,
  endTime: PropTypes.string,
  eventId: PropTypes.number,
  imageAlt: PropTypes.string,
  imageUrl: PropTypes.string,
  isEarnPeriodRecurring: PropTypes.bool,
  isFullDayEvent: PropTypes.bool,
  isRecurring: PropTypes.bool,
  lastModified: PropTypes.string,
  name: PropTypes.string,
  occurrenceDetails: PropTypes.string,
  parentEventId: PropTypes.number,
  promotionTypes: PropTypes.arrayOf(PropTypes.string),
  property: PropTypes.string,
  recurringPattern: recurringPatternShape,
  shortDescription: PropTypes.string,
  startDate: PropTypes.string,
  startTime: PropTypes.string,
  showOnCalendar: PropTypes.bool
});

export const baseRewardPropTypes = {
  rewardId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  quantity: PropTypes.number,
  redeemed: PropTypes.number,
  description: PropTypes.string,
  isNew: PropTypes.bool,
  startDateDisplay: PropTypes.string,
  endDateDisplay: PropTypes.string,
  startDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  redeemedDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  shouldShowRedeemButton: PropTypes.bool
};

export const patronRewardPropTypes = {
  ...baseRewardPropTypes,
  onlineOfferUuid: PropTypes.string,
  isSoftOffer: PropTypes.bool,
  prizeType: PropTypes.string,
  url: PropTypes.string,
  category: PropTypes.string
};

export const onlineOfferPropTypes = {
  ...baseRewardPropTypes,
  onlineOfferUuid: PropTypes.string.isRequired,
  isSoftOffer: PropTypes.bool.isRequired
};

export const plsRewardPropTypes = {
  ...baseRewardPropTypes,
  isPlsReward: PropTypes.bool,
  prizeType: PropTypes.string,
  dailyValidFromTime: PropTypes.string,
  dailyValidToTime: PropTypes.string,
  blackoutDates: PropTypes.arrayOf(PropTypes.string),
  allowsPartialRedemption: PropTypes.bool,
  totalValue: PropTypes.number,
  remainingValue: PropTypes.number,
  usedValue: PropTypes.number,
  category: PropTypes.string
};

export const partnerRewardPropTypes = {
  ...baseRewardPropTypes,
  isUberReward: PropTypes.bool,
  prizeType: PropTypes.string,
  prizeCodeLabel: PropTypes.string,
  canceled: PropTypes.bool,
  claimed: PropTypes.bool,
  location: PropTypes.string,
  amount: PropTypes.number,
  redeemLink: PropTypes.string
};
